import { Component, ViewEncapsulation, inject } from "@angular/core";
import { FormControl, ReactiveFormsModule, Validators } from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatInputModule } from "@angular/material/input";
import { DialogComponent } from "@components/layouts/dialog/dialog.component";
import { Messages } from "@config/messages";
import { AuthService } from "@services/auth.service";
import { LanguageService } from "@services/language.service";

export interface IRecallSignRequestDialogData {
  defaultRecalledBy?: string;
}

export interface IRecallSignRequestDialogResult {
  recallReason: string;
  recalledBy: string;
}

@Component({
  selector: "kzn-recall-sign-request-dialog",
  standalone: true,
  imports: [DialogComponent, MatFormFieldModule, MatInputModule, ReactiveFormsModule],
  templateUrl: "./recall-sign-request-dialog.component.html",
  styleUrl: "./recall-sign-request-dialog.component.scss",
  encapsulation: ViewEncapsulation.None,
})
export class RecallSignRequestDialogComponent {
  recallReason = new FormControl("", Validators.required);
  recalledBy = new FormControl("", Validators.required);
  Messages = Messages;

  private _languageService = inject(LanguageService);
  private _authService = inject(AuthService);
  private _dialogRef = inject(MatDialogRef<RecallSignRequestDialogComponent, IRecallSignRequestDialogResult | undefined>);
  private _data = inject<IRecallSignRequestDialogData>(MAT_DIALOG_DATA, { optional: true });

  constructor() {
    this.recalledBy.setValue(this.resolveRecalledByName());
    this.recalledBy.disable({ emitEvent: false });
  }

  get isFormInvalid(): boolean {
    return this.recallReason.invalid || this.recalledBy.invalid;
  }

  onAction(): void {
    this.recallReason.markAsTouched();
    if (this.isFormInvalid) return;

    this._dialogRef.close({
      recallReason: (this.recallReason.value ?? "").toString().trim(),
      recalledBy: (this.recalledBy.getRawValue() ?? "").toString().trim(),
    });
  }

  onSecondaryAction(): void {
    this._dialogRef.close(undefined);
  }

  t(key: string): string {
    return this._languageService.instant((key || "").toString());
  }

  private resolveRecalledByName(): string {
    const preset = this._data?.defaultRecalledBy?.trim();
    if (preset) return preset;

    const user = this._authService.getUserDetails();
    if (!user) return "";

    const name = [user.FirstName, user.LastName]
      .filter((part) => !!part?.trim())
      .join(" ")
      .trim();
    return name || user.Email || "";
  }
}
